import { Annotation } from '@readcloud/data';
import React from 'react';
import { HTMLAnnotationViewer } from './HTMLAnnotation/HTMLAnnotationViewer';
import { ImageAnnotationViewer } from './ImageAnnotation/ImageAnnotationViewer';
import { MediaAnnotationViewer } from './MediaAnnotation/MediaAnnotationViewer';
import { OverlayTextAnnotationViewer } from './OverlayTextAnnotation/OverlayTextAnnotationViewer';
import { PathAnnotationViewer } from './PathAnnotation/PathAnnotationViewer';
import { ShapeAnnotationViewer } from './ShapeAnnotation/ShapeAnnotationViewer';

interface InnerProps {
  dragging: boolean;
  editing: boolean;
  annotation: Annotation;
  height: number;
  width: number;
  onChange: (anno: Annotation) => void;
  scale: number;
  selected: boolean;
}
const AnnotationViewerInner: React.FC<InnerProps> = (props) => {
  /**
   * Use this to change annotation.data.text (for OverlayText type)
   */
  const onOverlayTextChange = React.useCallback(
    (text: string) => {
      if (props.annotation.type === 'OverlayText')
        props.onChange({
          ...props.annotation,
          data: {
            ...props.annotation.data,
            text: text,
          },
        });
    },
    [props.onChange, props.annotation, props.editing]
  );

  switch (props.annotation.type) {
    case 'Media':
      return (
        <MediaAnnotationViewer
          selected={props.selected}
          editing={props.editing}
          type={props.annotation.type}
          data={props.annotation.data}
          dragging={props.dragging}
          height={props.height}
          width={props.width}
          scale={props.scale}
          id={props.annotation.id}
        />
      );
    case 'HTML':
      return (
        <HTMLAnnotationViewer
          selected={props.selected}
          editing={props.editing}
          type={props.annotation.type}
          data={props.annotation.data}
          dragging={props.dragging}
          height={props.height}
          width={props.width}
          id={props.annotation.id}
          scale={props.scale}
        />
      );
    case 'Image':
      return (
        <ImageAnnotationViewer
          selected={props.selected}
          editing={props.editing}
          type={props.annotation.type}
          data={props.annotation.data}
          dragging={props.dragging}
          height={props.height}
          width={props.width}
          id={props.annotation.id}
          scale={props.scale}
        />
      );
    case 'Circle':
    case 'Rectangle':
      return (
        <ShapeAnnotationViewer
          selected={props.selected}
          editing={props.editing}
          type={props.annotation.type}
          data={props.annotation.data}
          dragging={props.dragging}
          height={props.height}
          width={props.width}
          id={props.annotation.id}
          scale={props.scale}
        />
      );

    case 'OverlayText':
      return (
        <OverlayTextAnnotationViewer
          selected={props.selected}
          editing={props.editing}
          onTextChange={onOverlayTextChange}
          type={props.annotation.type}
          data={props.annotation.data}
          dragging={props.dragging}
          height={props.height}
          width={props.width}
          id={props.annotation.id}
          scale={props.scale}
        />
      );
    case 'Path':
      return (
        <PathAnnotationViewer
          selected={props.selected}
          editing={props.editing}
          type={props.annotation.type}
          data={props.annotation.data}
          dragging={props.dragging}
          height={props.height}
          width={props.width}
          id={props.annotation.id}
          scale={props.scale}
        />
      );
  }
};

/**
 * This component is responsible for rendering the guts/inner content of an annotation.
 * I was trying to separate the concerns between RnD (resize and dragging) and appearance/feel of the actual annotation
 */

interface Props {
  dragging: boolean;
  editing: boolean;
  innerRef?: React.RefObject<HTMLDivElement>;
  onChange: (anno: Annotation) => void;
  annotation: Annotation;
  height: number;
  width: number;
  scale: number;
  onMouseDown?: JSX.IntrinsicElements['div']['onMouseDown'];
  onTouchStart?: JSX.IntrinsicElements['div']['onTouchStart'];
  selected: boolean;
}

export const AnnotationViewer: React.FC<Props> = (props) => {
  const { innerRef, ...other } = props;

  return (
    <div
      onMouseDown={props.onMouseDown}
      onTouchStart={props.onTouchStart}
      style={{
        height: props.height,
        width: props.width,
      }}
      ref={innerRef}
    >
      <AnnotationViewerInner {...other} />
    </div>
  );
};
